const allowedTags = new Set([
  "a", "abbr", "b", "blockquote", "br", "code", "del", "div", "em", "font",
  "h1", "h2", "h3", "h4", "h5", "h6", "hr", "i", "img", "li", "ol", "p",
  "pre", "s", "small", "span", "strike", "strong", "sub", "sup", "table", "tbody",
  "td", "tfoot", "th", "thead", "tr", "u", "ul", "caption", "col", "colgroup",
]);

const voidTags = new Set(["br", "col", "hr", "img"]);
const contentRemovingTags = ["script", "style", "template", "iframe", "object", "embed", "svg", "math", "form", "button", "textarea", "select"];
const allowedStyleProperties = new Set([
  "border-style", "border-width",
  "font-family", "font-size", "font-style", "font-weight", "height", "letter-spacing", "line-height",
  "margin", "margin-bottom", "margin-left", "margin-right", "margin-top", "max-height", "max-width", "min-height", "min-width",
  "padding", "padding-bottom", "padding-left", "padding-right", "padding-top", "text-align", "text-indent",
  "vertical-align", "white-space", "width", "word-break", "word-spacing", "word-wrap",
]);

const globalAttributes = new Set(["dir", "lang", "style", "title"]);
const tagAttributes: Record<string, Set<string>> = {
  a: new Set(["href", "name", "target"]),
  blockquote: new Set(["cite", "type"]),
  font: new Set(["face", "size"]),
  img: new Set(["alt", "height", "src", "title", "width"]),
  li: new Set(["value"]),
  ol: new Set(["reversed", "start", "type"]),
  col: new Set(["span", "width"]),
  colgroup: new Set(["span", "width"]),
  table: new Set(["align", "cellpadding", "cellspacing", "height", "width"]),
  td: new Set(["align", "colspan", "height", "rowspan", "valign", "width"]),
  th: new Set(["align", "colspan", "height", "rowspan", "scope", "valign", "width"]),
};

function decodeAttribute(value: string): string {
  return value.replace(/&(#(?:x[0-9a-f]+|\d+)|amp|quot|apos|lt|gt);?/gi, (entity, code: string) => {
    const named: Record<string, string> = { amp: "&", quot: '"', apos: "'", lt: "<", gt: ">" };
    const normalized = code.toLowerCase();
    if (named[normalized]) return named[normalized];
    const numeric = normalized.startsWith("#x") ? Number.parseInt(normalized.slice(2), 16) : Number.parseInt(normalized.slice(1), 10);
    return Number.isFinite(numeric) ? String.fromCodePoint(numeric) : "";
  });
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function safeUrl(value: string, image = false): string | null {
  const decoded = decodeAttribute(value).trim().replace(/[\u0000-\u001f\u007f]/g, "");
  if (image) return /^https?:\/\//i.test(decoded) ? decoded : null;
  return /^(?:https?:|mailto:|tel:)/i.test(decoded) || decoded.startsWith("#") || decoded.startsWith("/") ? decoded : null;
}

function safeStyle(value: string): string {
  const declarations: string[] = [];
  for (const declaration of decodeAttribute(value).split(";")) {
    const separator = declaration.indexOf(":");
    if (separator < 1) continue;
    const property = declaration.slice(0, separator).trim().toLowerCase();
    const styleValue = declaration.slice(separator + 1).trim();
    if (!allowedStyleProperties.has(property) || !styleValue || styleValue.length > 300) continue;
    if (/[<>{}]/.test(styleValue) || /(?:url\s*\(|expression\s*\(|@import|behavior\s*:|-moz-binding)/i.test(styleValue)) continue;
    declarations.push(`${property}:${styleValue}`);
  }
  return declarations.join(";");
}

function allowedClass(value: string): string | null {
  // Keep only source-email markers. Gmail UI classes (adm/adL/im, etc.)
  // are not part of the message and must not be used to force content hidden.
  const classes = value.split(/\s+/).filter((item) => ["agent-signature", "gmail_quote", "gmail_extra", "gmail_attr", "gmail_quote_container", "gmail_signature", "gmail_signature_prefix"].includes(item));
  return classes.length ? classes.join(" ") : null;
}

function sanitizeTag(tagName: string, rawAttributes: string, closing: boolean): string {
  const tag = tagName.toLowerCase();
  if (!allowedTags.has(tag)) return "";
  if (closing) return voidTags.has(tag) ? "" : `</${tag}>`;

  const attributes: string[] = [];
  const attributePattern = /([^\s=\/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  let match: RegExpExecArray | null;
  while ((match = attributePattern.exec(rawAttributes))) {
    const name = match[1].toLowerCase();
    const rawValue = match[2] ?? match[3] ?? match[4] ?? "";
    if (name.startsWith("on") || name === "xmlns") continue;
    if (name === "class") {
      const value = allowedClass(decodeAttribute(rawValue));
      if (value) attributes.push(`class="${escapeAttribute(value)}"`);
      continue;
    }
    if (name === "id" && decodeAttribute(rawValue).toLowerCase() === "divrplyfwdmsg") {
      attributes.push('id="divRplyFwdMsg"');
      continue;
    }
    if (!globalAttributes.has(name) && !tagAttributes[tag]?.has(name)) continue;
    if (name === "style") {
      const value = safeStyle(rawValue);
      if (value) attributes.push(`style="${escapeAttribute(value)}"`);
      continue;
    }
    if (name === "href" || name === "cite" || name === "src") {
      const value = safeUrl(rawValue, name === "src");
      if (value) attributes.push(`${name}="${escapeAttribute(value)}"`);
      continue;
    }
    if (name === "target") continue;
    if (name === "reversed") { attributes.push("reversed"); continue; }
    const value = decodeAttribute(rawValue).slice(0, 500);
    if (value && !/[<>]/.test(value)) attributes.push(`${name}="${escapeAttribute(value)}"`);
  }
  if (tag === "a" && attributes.some((attribute) => attribute.startsWith("href="))) attributes.push('target="_blank"', 'rel="noopener noreferrer nofollow"');
  if (tag === "img") {
    if (!attributes.some((attribute) => attribute.startsWith("src="))) return "";
    attributes.push('loading="lazy"', 'referrerpolicy="no-referrer"');
  }
  return `<${tag}${attributes.length ? ` ${attributes.join(" ")}` : ""}>`;
}

export function sanitizeRichHtml(input: string | undefined, maxLength = 1_000_000): string | null {
  if (!input) return null;
  let html = input.slice(0, maxLength).replace(/<!--[\s\S]*?-->/g, "").replace(/<!DOCTYPE[^>]*>/gi, "");
  for (const tag of contentRemovingTags) {
    html = html.replace(new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}\\s*>`, "gi"), "");
    html = html.replace(new RegExp(`<${tag}\\b[^>]*\\/?\\s*>`, "gi"), "");
  }
  const sanitized = html.replace(/<\s*(\/?)\s*([a-z][a-z0-9:-]*)\b([^>]*)>/gi, (_whole, slash: string, tag: string, attributes: string) => sanitizeTag(tag, attributes, Boolean(slash)));
  return sanitized.trim() || null;
}
